/**
 * ProductGallery — Gymshark scroll-grid style
 * Bento grid, pill scrollbar, inline click-to-zoom with pan.
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var ZOOM_SCALE = 2.5;

  function ProductGallery(container) {
    if (!container) return;
    this.container = typeof container === 'string'
      ? document.querySelector(container)
      : container;
    if (!this.container) return;

    this.grid = this.container.querySelector('.pdp-gallery__grid');
    this.cells = this.container.querySelectorAll('.pdp-gallery__cell');
    this.pillTrack = this.container.querySelector('.pdp-gallery__pill-track');
    this.pillThumb = this.container.querySelector('.pdp-gallery__pill-thumb');
    this.arrowUp = this.container.querySelector('.pdp-gallery__scroll-arrow--up');
    this.arrowDown = this.container.querySelector('.pdp-gallery__scroll-arrow--down');
    this.dots = this.container.querySelectorAll('.pdp-gallery__dot');
    this.cursor = this.container.querySelector('.pdp-gallery__cursor');

    this._boundHandlers = {};
    this._rafId = null;
    this._zoomedCell = null;
    this._isTouch = window.matchMedia('(pointer: coarse)').matches;

    this._bindEvents();
    this._updateScrollbar();
  }

  ProductGallery.prototype = {

    /* ---------- Public API ---------- */

    switchToImageId: function (imageId) {
      var id = String(imageId);
      for (var i = 0; i < this.cells.length; i++) {
        if (this.cells[i].getAttribute('data-image-id') === id) {
          this._scrollToCell(i);
          return;
        }
      }
    },

    destroy: function () {
      var h = this._boundHandlers;
      if (h.scroll) window.removeEventListener('scroll', h.scroll);
      if (this.arrowUp && h.arrowUp) this.arrowUp.removeEventListener('click', h.arrowUp);
      if (this.arrowDown && h.arrowDown) this.arrowDown.removeEventListener('click', h.arrowDown);
      if (h.keydown) document.removeEventListener('keydown', h.keydown);
      if (this.grid && h.mobileScroll) this.grid.removeEventListener('scroll', h.mobileScroll);
      if (this.pillTrack && h.trackClick) this.pillTrack.removeEventListener('click', h.trackClick);

      for (var i = 0; i < this.cells.length; i++) {
        var c = this.cells[i];
        if (c._zoomEnter) c.removeEventListener('mouseenter', c._zoomEnter);
        if (c._zoomClick) c.removeEventListener('click', c._zoomClick);
        if (c._zoomMove) c.removeEventListener('mousemove', c._zoomMove);
        if (c._zoomLeave) c.removeEventListener('mouseleave', c._zoomLeave);
      }

      if (this._rafId) cancelAnimationFrame(this._rafId);
    },

    /* ---------- Private ---------- */

    _bindEvents: function () {
      var self = this;

      // Scroll → update pill thumb
      this._boundHandlers.scroll = function () {
        if (self._rafId) return;
        self._rafId = requestAnimationFrame(function () {
          self._updateScrollbar();
          self._rafId = null;
        });
      };
      window.addEventListener('scroll', this._boundHandlers.scroll, { passive: true });

      // Arrow clicks
      if (this.arrowUp) {
        this._boundHandlers.arrowUp = function () { self._scrollByRow(-1); };
        this.arrowUp.addEventListener('click', this._boundHandlers.arrowUp);
      }
      if (this.arrowDown) {
        this._boundHandlers.arrowDown = function () { self._scrollByRow(1); };
        this.arrowDown.addEventListener('click', this._boundHandlers.arrowDown);
      }

      // Click on track to jump
      if (this.pillTrack) {
        this._boundHandlers.trackClick = function (e) {
          var rect = self.pillTrack.getBoundingClientRect();
          var clickRatio = (e.clientY - rect.top) / rect.height;
          clickRatio = Math.max(0, Math.min(1, clickRatio));
          self._scrollToRatio(clickRatio);
        };
        this.pillTrack.addEventListener('click', this._boundHandlers.trackClick);
      }

      // Inline zoom on image cells (desktop only)
      if (!this._isTouch) {
        for (var i = 0; i < this.cells.length; i++) {
          this._bindCellZoom(this.cells[i]);
        }
      }

      // ESC to close zoom
      this._boundHandlers.keydown = function (e) {
        if (e.key === 'Escape' && self._zoomedCell) {
          self._zoomOut(self._zoomedCell);
        }
      };
      document.addEventListener('keydown', this._boundHandlers.keydown);

      // Mobile dots
      if (this.grid && this.dots.length > 0) {
        this._boundHandlers.mobileScroll = function () { self._updateDots(); };
        this.grid.addEventListener('scroll', this._boundHandlers.mobileScroll, { passive: true });
        for (var d = 0; d < this.dots.length; d++) {
          (function (idx) {
            self.dots[idx].addEventListener('click', function () {
              self._scrollToMobileSlide(idx);
            });
          })(d);
        }
        this._updateDots();
      }
    },

    /* -- Inline zoom -- */

    _bindCellZoom: function (cell) {
      if (cell.getAttribute('data-media-type') !== 'image') return;

      var self = this;
      var img = cell.querySelector('img');
      if (!img) return;

      // Mouseenter → show cursor, preload hi-res
      cell._zoomEnter = function () {
        if (self.cursor) {
          self.cursor.classList.add('is-visible');
          if (cell.classList.contains('is-zoomed')) {
            self.cursor.classList.add('is-zoomed');
          }
        }
        var zoomUrl = cell.getAttribute('data-zoom-url');
        if (zoomUrl && !img._hiresLoaded) {
          var preload = new Image();
          preload.src = zoomUrl;
          img._hiresLoaded = true;
        }
      };
      cell.addEventListener('mouseenter', cell._zoomEnter);

      // Click → toggle zoom
      cell._zoomClick = function (e) {
        e.preventDefault();
        if (cell.classList.contains('is-zoomed')) {
          self._zoomOut(cell);
        } else {
          if (self._zoomedCell && self._zoomedCell !== cell) {
            self._zoomOut(self._zoomedCell);
          }
          self._zoomIn(cell, e);
        }
      };
      cell.addEventListener('click', cell._zoomClick);

      // Mousemove → move cursor + pan while zoomed
      cell._zoomMove = function (e) {
        if (self.cursor) {
          self.cursor.style.left = e.clientX + 'px';
          self.cursor.style.top = e.clientY + 'px';
        }
        if (!cell.classList.contains('is-zoomed')) return;
        var rect = cell.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      };
      cell.addEventListener('mousemove', cell._zoomMove);

      // Mouseleave → hide cursor, zoom out
      cell._zoomLeave = function () {
        if (self.cursor) {
          self.cursor.classList.remove('is-visible');
          self.cursor.classList.remove('is-zoomed');
        }
        if (cell.classList.contains('is-zoomed')) {
          self._zoomOut(cell);
        }
      };
      cell.addEventListener('mouseleave', cell._zoomLeave);
    },

    _zoomIn: function (cell, e) {
      var img = cell.querySelector('img');
      if (!img) return;

      var zoomUrl = cell.getAttribute('data-zoom-url');
      if (zoomUrl) img.src = zoomUrl;

      var rect = cell.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
      img.style.transform = 'scale(' + ZOOM_SCALE + ')';

      cell.classList.add('is-zoomed');
      this._zoomedCell = cell;

      // Switch cursor to zoomed state
      if (this.cursor) this.cursor.classList.add('is-zoomed');
    },

    _zoomOut: function (cell) {
      var img = cell.querySelector('img');
      if (!img) return;

      img.style.transform = '';
      img.style.transformOrigin = '';
      cell.classList.remove('is-zoomed');

      if (this._zoomedCell === cell) this._zoomedCell = null;

      // Switch cursor back to normal
      if (this.cursor) this.cursor.classList.remove('is-zoomed');
    },

    /* -- Pill scrollbar -- */

    _updateScrollbar: function () {
      if (!this.grid || !this.pillThumb || !this.pillTrack) return;

      var rect = this.grid.getBoundingClientRect();
      var viewH = window.innerHeight;
      var gridH = rect.height;

      if (gridH <= viewH) {
        this.pillThumb.style.opacity = '0';
        return;
      }
      this.pillThumb.style.opacity = '1';

      var scrolled = (-rect.top) / (gridH - viewH);
      scrolled = Math.max(0, Math.min(1, scrolled));

      var trackH = this.pillTrack.offsetHeight;
      var thumbRatio = Math.min(viewH / gridH, 0.45);
      var thumbH = Math.max(14, trackH * thumbRatio);
      var maxTop = trackH - thumbH;

      this.pillThumb.style.height = thumbH + 'px';
      this.pillThumb.style.top = (scrolled * maxTop) + 'px';

      if (this.arrowUp) this.arrowUp.disabled = scrolled <= 0.01;
      if (this.arrowDown) this.arrowDown.disabled = scrolled >= 0.99;
    },

    _scrollByRow: function (direction) {
      if (!this.cells.length) return;
      var rowH = this.cells[0].offsetHeight + 4;
      window.scrollBy({ top: direction * rowH, behavior: 'smooth' });
    },

    _scrollToRatio: function (ratio) {
      if (!this.grid) return;
      var rect = this.grid.getBoundingClientRect();
      var gridH = rect.height;
      var viewH = window.innerHeight;
      var targetScrollTop = rect.top + window.pageYOffset + ratio * (gridH - viewH);
      window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    },

    _scrollToCell: function (index) {
      var cell = this.cells[index];
      if (!cell) return;
      var headerH = 0;
      var header = document.querySelector('.site-header');
      if (header) headerH = header.offsetHeight;
      var top = cell.getBoundingClientRect().top + window.pageYOffset - headerH;
      window.scrollTo({ top: top, behavior: 'smooth' });
    },

    /* -- Mobile dots -- */

    _updateDots: function () {
      if (!this.grid || !this.dots.length) return;
      var scrollLeft = this.grid.scrollLeft;
      var cellWidth = this.grid.offsetWidth;
      var activeIdx = Math.round(scrollLeft / cellWidth);
      for (var i = 0; i < this.dots.length; i++) {
        this.dots[i].classList.toggle('is-active', i === activeIdx);
      }
    },

    _scrollToMobileSlide: function (index) {
      if (!this.grid) return;
      this.grid.scrollTo({ left: index * this.grid.offsetWidth, behavior: 'smooth' });
    }
  };

  window.ProductGallery = ProductGallery;
})();
